import React from 'react';
import { View, Platform, StyleSheet } from 'react-native';
import parseByDelimiter from '@utils/parseByDelimiter';
import toTitleCase from '@hooks/toTitleCase';
import { MaterialIcons } from '@expo/vector-icons';
import ErrorBoundary from '@utils/ErrorBoundary';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { SizableText } from 'tamagui';

const DefaultDataItem = ({ item, navigation }) => {
  const theme = themeSelector();

  const dataObject = item?.data || {};
  const keys = Object.keys(dataObject);
  const keyValuePairs = [];

  if (keys.length > 0) {
    // Prioritize fields with 'name' in them
    const nameFields = keys.filter(key => key.toLowerCase().includes('name'));
    const otherFields = keys.filter(key => !key.toLowerCase().includes('name'));
    
    // Sort: name fields first, then others
    const sortedKeys = [...nameFields, ...otherFields];
    
    for (let i = 0; i < Math.min(3, sortedKeys.length); i++) {
      const key = sortedKeys[i];
      const value = toTitleCase(dataObject[key]?.toString() || "");
      keyValuePairs?.push({ key, value });
    }
  }

  // Check if this is a POI import (company/business)
  const isPOIImport = item?.enrichmentMetadata?.source === 'networking_map' || 
                      item?.enrichmentMetadata?.poi_type || 
                      (item?.data && (item.data.business_name || item.data.company_name || item.data.display_name));

  return (
    <ErrorBoundary>
      <View
        style={[
          modernStyles.mainContainer,
          {
            backgroundColor: theme ? "white" : "#1e293b",
            padding: 15,
            paddingHorizontal: 20,
          }
        ]}
      >
        {/* Company icon indicator for POI imports */}
        {isPOIImport && (
          <View style={[
            modernStyles.poiBadge,
            {
              backgroundColor: theme ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.25)',
            }
          ]}>
            <MaterialIcons
              name="business"
              size={18}
              color={theme ? '#3b82f6' : '#60a5fa'}
            />
          </View>
        )}

        <View style={modernStyles.contentContainer}>
          {keyValuePairs.length > 0 ? (
            keyValuePairs.map(({ key, value }) => {
              return (
                <View
                  key={key}
                  style={[
                    modernStyles.infoRow,
                    {
                      backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                    }
                  ]}
                >
                  <SizableText
                    style={[
                      modernStyles.fieldLabel,
                    ]}
                  >
                    {parseByDelimiter(key)}
                  </SizableText>
                  <SizableText
                    numberOfLines={2}
                    style={[
                      modernStyles.fieldValue,
                      {
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontStyle: value === "No Data" ? 'italic' : 'normal',
                        opacity: value === "No Data" ? 0.6 : 1
                      }
                    ]}
                  >
                    {value}
                  </SizableText>
                </View>
              )
            })
          ) : (
            <View style={modernStyles.emptyState}>
              <SizableText
                style={[
                  modernStyles.emptyStateText,
                ]}
              >
                No data available
              </SizableText>
            </View>
          )}
        </View>
      </View>
    </ErrorBoundary>
  )
}

// Modern styling system matching DefaultCustomer and DefaultProduct
const modernStyles = StyleSheet.create({
  mainContainer: {
    borderRadius: 20,
    alignSelf: 'stretch',
    overflow: 'visible',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    }),
  },
  contentContainer: {
    gap: 12,
    alignItems: 'stretch',
  },
  infoRow: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    marginVertical: 2,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldValue: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  poiBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 10,
    borderRadius: 16,
    padding: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  emptyState: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    fontStyle: 'italic',
  },
});

export default DefaultDataItem;